/**
 * Public entity storefront section visibility.
 *
 * Canonical signal: published, public-visible shop catalog products returned by
 * userApi/shop/products for the entity scope (organization_id or creator_id).
 * No duplicate feature flag beyond the platform Shop master toggle — if the
 * catalog has displayable products, the account has a configured public
 * storefront presence; otherwise render nothing (no empty / placeholder UI).
 */

import { isShopSectionEnabled } from "../../Helper/siteContent";

/**
 * Client-side guard on shop/products payloads. The public API already scopes to
 * published + public-visible rows; this rejects malformed entries and any
 * draft / private / deleted / disabled fields if they appear in alternate payloads.
 *
 * @param {unknown} products
 * @returns {Array<object>}
 */
export function filterEntityPublicShopProducts(products) {
  if (!Array.isArray(products)) {
    return [];
  }
  return products.filter((product) => {
    if (!product || typeof product !== "object") {
      return false;
    }
    if (product.id == null) {
      return false;
    }
    const slug = String(product.slug || "").trim();
    if (!slug) {
      return false;
    }
    if (product.deleted_at != null && product.deleted_at !== "") {
      return false;
    }
    if (product.status != null) {
      const status = String(product.status).toLowerCase();
      if (status !== "published") {
        return false;
      }
    }
    if (product.visibility != null) {
      const visibility = String(product.visibility).toLowerCase();
      if (visibility !== "public") {
        return false;
      }
    }
    if (product.is_published === false || product.is_public === false) {
      return false;
    }
    if (product.enabled === false || product.disabled === true) {
      return false;
    }
    return true;
  });
}

/**
 * @param {unknown} products
 * @returns {boolean}
 */
export function entityHasPublicStorefrontCatalog(products) {
  return filterEntityPublicShopProducts(products).length > 0;
}

/**
 * Gate the public Storefront section (heading + body). Hide while loading,
 * when the platform Shop section is disabled, and when the catalog has no
 * publicly displayable products — no empty shop / placeholder / coming-soon UI.
 *
 * Workspace- or Admin-created storefronts only surface here when the scoped
 * shop/products response includes at least one displayable product. Entity
 * type alone never shows the section.
 *
 * @param {{ products?: unknown, productsLoading?: boolean }} opts
 * @returns {boolean}
 */
export function shouldRenderEntityStorefrontSection(opts) {
  const options = opts && typeof opts === "object" ? opts : {};
  if (!isShopSectionEnabled()) {
    return false;
  }
  if (options.productsLoading) {
    return false;
  }
  return entityHasPublicStorefrontCatalog(options.products);
}

export default shouldRenderEntityStorefrontSection;
